import { gql } from "@apollo/client";
import {
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControlLabel,
  Switch,
} from "@mui/material";
import Grid from "@mui/material/Grid2";

import React, { useState } from "react";
import { useCreateSupplyItemMutation, useUpdateSupplyItemMutation } from "~/graphql/__generated__/graphql";

type SupplyItem = {
  id: number,
  label: string,
  barcode: string,
  currentStock: number,
  active: boolean,
}

type Props = {
  onClose: () => void;
  supplyItem: SupplyItem;
}

const EditSupplyItem: React.FC<Props> = ({ onClose, supplyItem }) => {
  const [edited, setEdited] = useState({ ...supplyItem });

  const [createSupplyItem, createStatus] = useCreateSupplyItemMutation();
  const [updateSupplyItem, updateStatus] = useUpdateSupplyItemMutation();
  const loading = createStatus.loading || updateStatus.loading;

  const onSave = async () => {
    const { id, currentStock, ...rest } = edited
    if (edited.id) {
      await updateSupplyItem({
        variables: { supplyItemId: edited.id, ...rest },
      });
    } else {
      await createSupplyItem({
        variables: { currentStock, ...rest },
        awaitRefetchQueries: true,
        refetchQueries: ["SupplyItemsPage"],
      });
    }

    onClose();
  }

  return (<Dialog open={true} onClose={onClose} maxWidth="xs">
    <DialogTitle>
      {supplyItem.id ? `Editing ${supplyItem.label}` : "Add Supply Item"}
    </DialogTitle>
    <DialogContent>
      <Grid container spacing={2} sx={{ mt: 1 }}>
        <Grid size={12}>
          <TextField
            required
            label="Label"
            fullWidth
            size="small"
            value={edited.label}
            onChange={(e) => setEdited({...edited, label: e.target.value })}
            disabled={loading}
          />
        </Grid>

        <Grid size={12}>
          <TextField
            label="Barcode"
            size="small"
            fullWidth
            autoCorrect="off"
            value={edited.barcode}
            onChange={(e) => setEdited({ ...edited, barcode: e.target.value })}
            disabled={loading}
          />
        </Grid>

        {!edited.id && <Grid size={12}>
          <TextField
            required
            label="Current Stock"
            type="number"
            size="small"
            fullWidth
            autoCorrect="off"
            value={edited.currentStock}
            onChange={(e) => setEdited({
              ...edited,
              currentStock: parseInt(e.target.value) > 0 ? parseInt(e.target.value) : 0,
            })}
            disabled={loading}
          />
        </Grid>}

        {!!edited.id && <FormControlLabel
          control={<Switch
            checked={edited.active}
            onChange={() => setEdited({ ...edited, active: !edited.active })}
          />}
          label="Active"
        />}
      </Grid>
    </DialogContent>
    <DialogActions>
      <Button disabled={loading} onClick={onClose}>
        Cancel
      </Button>
      <Button loading={loading} disabled={!edited.label} onClick={onSave} color="primary">
        Save
      </Button>
    </DialogActions>
  </Dialog>);
}

export default EditSupplyItem;

gql(`
  mutation CreateSupplyItem(
    $label: String!
    $barcode: String!
    $currentStock: Int!
  )
  @permission(requires: manageSupplies)
  {
    createSupplyItem(
      label: $label
      barcode: $barcode
      currentStock: $currentStock
    ) {
      id
    }
  }
`);

gql(`
  mutation UpdateSupplyItem(
    $supplyItemId: Int!
    $label: String!
    $barcode: String!
    $active: Boolean!
  )
  @permission(requires: manageSupplies)
  {
    updateSupplyItem(
      id: $supplyItemId
      label: $label
      barcode: $barcode
      active: $active
    ) {
      id
      label
      barcode
      currentStock
      active
    }
  }
`);
